import ReactMarkdown from "react-markdown";
import type { ReactNode } from "react";
import { slugifyHeading } from "../../utils";
import { Typography } from "@/components/ui/atoms";

interface RichTextContentProps {
  markdown: string;
}

function headingText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(headingText).join(" ");
  if (children && typeof children === "object" && "props" in children) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return headingText((children as any).props?.children);
  }
  return "";
}

function paragraphHasImageNode(node: unknown): boolean {
  if (!node || typeof node !== "object" || !("children" in node)) return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const children = (node as any).children;
  if (!Array.isArray(children)) return false;

  return children.some((child) => {
    if (!child || typeof child !== "object") return false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (child as any).tagName === "img" || (child as any).type === "image";
  });
}

export function RichTextContent({ markdown }: RichTextContentProps) {
  return (
    <div className="text-base leading-7 text-dark-invirtus/90 lg:text-[1.02rem] [&_h2]:mt-7 [&_h2]:mb-2 [&_h2]:text-[1.55rem] [&_h2]:font-bold [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-[1.25rem] [&_h3]:font-semibold [&_p]:my-3 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_img]:my-5 [&_img]:h-auto [&_img]:w-full [&_img]:max-h-[380px] [&_img]:rounded-lg [&_img]:bg-white [&_img]:object-contain [&_video]:my-5 [&_video]:h-auto [&_video]:w-full [&_video]:max-h-[380px] [&_video]:rounded-lg [&_video]:bg-white [&_video]:object-contain [&_iframe]:my-5 [&_iframe]:h-[320px] [&_iframe]:w-full [&_iframe]:rounded-lg [&_iframe]:bg-white [&_a]:text-dark-invirtus [&_a]:underline [&_a]:underline-offset-4">
      <ReactMarkdown
        components={{
          h2: ({ children }) => {
            const text = headingText(children);
            return <Typography variant="h2" id={slugifyHeading(text)}>{children}</Typography>;
          },
          h3: ({ children }) => {
            const text = headingText(children);
            return <Typography variant="h3" id={slugifyHeading(text)}>{children}</Typography>;
          },
          p: ({ children, node }) =>
            paragraphHasImageNode(node) ? (
              <div className="my-3">{children}</div>
            ) : (
              <Typography as="p" variant="body-md" color="secondary" weight="medium">
                {children}
              </Typography>
            ),
          ul: ({ children }) => <Typography as="ul" variant="body-md" color="secondary" weight="medium">{children}</Typography>,
          ol: ({ children }) => <Typography as="ol" variant="body-md" color="secondary" weight="medium">{children}</Typography>,
          blockquote: ({ children }) => <Typography as="blockquote" variant="body-md" color="secondary" weight="medium">{children}</Typography>,
          a: ({ href, children }) => <Typography as="a" variant="body-md" color="secondary" weight="medium" href={href} target="_blank" rel="noreferrer">{children}</Typography>,
          img: ({ src, alt }) => (
            <figure>
              <img src={src || ""} alt={alt || ""} loading="lazy" />
            </figure>
          ),
        }}
      >
        {markdown || ""}
      </ReactMarkdown>
    </div>
  );
}
